/**
 * The theme the app had before SF-217, kept unchanged for the management console, whose entry
 * point (frontend/management-console/src/index.tsx) passes no theme to the provider. Moved here
 * from context/ThemeContext.tsx as it was at b2c228db0; ./__tests__/legacyTheme.test.ts pins its
 * values so the console cannot change by accident.
 */
import { createTheme, type Theme } from '@mui/material/styles';

import type { ThemeMode } from './tokens';

export const legacyTheme = (mode: ThemeMode): Theme => {
  const isLight = mode === 'light';
  return createTheme({
    palette: {
      mode,
      // Stratforth brand palette. Deep navy primary conveys authority/trust for a
      // risk console; teal secondary is the modern accent. Replaces the MUI factory
      // blue/pink defaults. Severity colours (error/warning/success) are left to MUI
      // so risk status keeps conventional, accessible signalling.
      primary: {
        main: isLight ? '#17324D' : '#5C90C9',
        light: isLight ? '#3E5C7E' : '#8AB4E0',
        dark: isLight ? '#0C2036' : '#3E5C7E',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: isLight ? '#1FA39A' : '#4FC9C0',
        light: isLight ? '#5FD3C9' : '#8FE3DC',
        dark: isLight ? '#0E6E67' : '#1FA39A',
        contrastText: isLight ? '#FFFFFF' : '#062B29',
      },
      background: {
        default: isLight ? '#F4F6F9' : '#0E1620',
        paper: isLight ? '#FFFFFF' : '#16212E',
      },
    },
    typography: {
      // No external font CDN — a data-residency requirement for a risk platform. The
      // console's entry imports no font file, so it draws in Inter only where the machine
      // has it, and otherwise in the next face of this list.
      fontFamily: ['Inter', 'system-ui', '-apple-system', 'Segoe UI', 'Arial', 'sans-serif'].join(','),
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: '1px solid',
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid',
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
          },
        },
      },
    },
  });
};
